import { computed, onUnmounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import alertsService, { ALERTS_PAGE_SIZE } from '@/services/alerts.service'
import { getErrorMessage } from '@/utils/error-message'

/**
 * THE account-alerts store. One fetch loop, one list, behind the notification
 * bell in the app header.
 *
 * These are notices a HUMAN on the support team wrote about this specific
 * account — "we had to change X on your order". They are not system chatter, so
 * two rules fall out of that and are enforced here rather than in the views:
 *
 *  1. The list is the same everywhere. A module singleton, exactly like
 *     useVoteActivity, so the badge on the bell and the rows in the dropdown
 *     can never disagree, and the count survives navigation without a refetch
 *     that would blink it.
 *  2. A failure is silent. See `refresh`.
 *
 * ── HIDDEN TABS DO NOT POLL ───────────────────────────────────────────────────
 * Copied deliberately from useVoteActivity's `onVisibilityChange`, because it is
 * a product rule here and not an optimisation local to that file: a backgrounded
 * tab STOPS (the timer is cleared) rather than skipping a tick and rescheduling,
 * so a panel left open overnight costs nothing. Coming back fetches once
 * immediately — the list on screen is by definition as old as the time away —
 * and that fetch re-arms the cycle, so the cadence resumes from a known point.
 *
 * `visibilitychange` is the only signal, for the reason spelled out there:
 * window blur/focus fire when the window merely loses keyboard focus while still
 * fully on screen, which would freeze a panel the user is still looking at.
 */

/*
 * Slow on purpose. An alert is written by a person, minutes to hours before the
 * customer reads it, so five minutes late is indistinguishable from instant —
 * and the whole point of the poll is only to catch one that arrives while the
 * panel is already open.
 */
const POLL_INTERVAL_MS = 300_000

/** The one controller. Rebuilt only if whoever created it is torn down. */
let controller = null

const SEVERITIES = new Set(['info', 'warning', 'action_required'])

/**
 * One alert from the API, reduced to what the card renders and with every field
 * given a usable type.
 *
 * Returns null for anything without an id: the id is the handle for read,
 * dismiss and the rollback, so an alert without one could be displayed but
 * never acted on — a card whose Dismiss button cannot work.
 */
const normalize = (raw) => {
  if (!raw || raw.id === undefined || raw.id === null) return null

  const severity = SEVERITIES.has(raw.severity) ? raw.severity : 'info'

  return {
    id: raw.id,
    severity,
    title: typeof raw.title === 'string' ? raw.title.trim() : '',
    body: typeof raw.body === 'string' ? raw.body.trim() : '',
    orderId: raw.order_id ?? null,
    orderLabel: typeof raw.order_label === 'string' ? raw.order_label.trim() : '',
    actionLabel: typeof raw.action_label === 'string' ? raw.action_label.trim() : '',
    actionUrl: typeof raw.action_url === 'string' ? raw.action_url.trim() : '',
    // Absent means no. A chat button offered on an alert support did not mark
    // as discussable sends someone into a conversation nobody has context for.
    allowChat: raw.allow_chat === true,
    createdAt: raw.created_at ?? null,
    readAt: raw.read_at ?? null,
  }
}

function createController() {
  const store = useStore()

  const isLoggedIn = computed(() => store.state.auth.status.loggedIn)

  /** The alerts on screen. Written only by a settled fetch or by a dismiss. */
  const alerts = ref([])

  /** First load only. A background refresh must not blank a rendered panel. */
  const loading = ref(false)

  /**
   * Set by a failed fetch, cleared by a successful one. Exposed for callers that
   * want it — the panel deliberately does NOT render it, see `refresh`.
   */
  const error = ref(null)

  /**
   * False once the backend has told us it cannot store alerts at all. In that
   * state the BELL ITSELF does not render — no empty dropdown, no "unavailable"
   * note. There is no such thing as an alerts feature the customer was promised
   * and is now missing: they have simply never seen one.
   */
  const available = ref(true)

  /** Is there an older page behind what is on screen? Drives "View previous". */
  const hasMore = ref(false)

  /** A "View previous" request is out. Separate from `loading`, which is the
      first load — the two render in completely different places. */
  const loadingMore = ref(false)

  /*
   * The badge number, taken from the SERVER's `unread_count` rather than
   * counted off `alerts`.
   *
   * Counting locally under-reports, and does it silently. The first page is 10
   * rows, so an account with 25 unread notices would wear a badge reading 10 —
   * and it would look completely plausible. The server counts every
   * non-dismissed, unexpired, unread row, not just the ones on screen.
   *
   * It is a ref rather than a computed because it is then adjusted locally by
   * `markRead`/`dismiss`: the badge has to drop the instant a row is read, not
   * five minutes later when the next poll lands. Every fetch re-syncs it, so
   * any local drift is corrected on the next poll rather than accumulating.
   */
  const serverUnreadCount = ref(0)

  const unreadCount = computed(() => Math.max(0, serverUnreadCount.value))

  const hasAlerts = computed(() => available.value && alerts.value.length > 0)

  /* ── Scheduling ─────────────────────────────────────────────────────────────
     `pollTimeout` is the ONLY timer, and every path that schedules goes through
     `schedule()`, which clears before it sets — so at any instant there is one
     pending poll or none, never two. */
  let pollTimeout = null

  /** The poll in the air, if any. Shared so a second caller joins it. */
  let inFlight = null

  /** Has the first fetch of this session settled? Gates the `loading` flag. */
  let loadedOnce = false

  /**
   * True once a "View previous" page has come back saying there is nothing
   * older. Tracked separately from `hasMore` because a background poll only
   * ever learns about the FIRST page — its `has_more` says "there is something
   * behind row 10", which is already on screen once the reader has paged, and
   * letting it write `hasMore` would make the exhausted link reappear every
   * five minutes.
   */
  let tailExhausted = false

  const isActive = () => isLoggedIn.value && !document.hidden

  const schedule = (delayMs) => {
    clearTimeout(pollTimeout)
    pollTimeout = setTimeout(() => {
      pollTimeout = null
      refresh()
    }, Math.max(0, delayMs))
  }

  /** Stop entirely: no timer at all until something starts it again. */
  const suspend = () => {
    clearTimeout(pollTimeout)
    pollTimeout = null
  }

  const armPoll = () => {
    if (!isActive()) {
      suspend()
      return
    }
    schedule(POLL_INTERVAL_MS)
  }

  /**
   * Fetch the alerts and re-arm.
   *
   * A FAILURE IS SILENT — no toast, ever. This is a background poll the customer
   * did not ask for, running on every screen; an alert system that interrupts to
   * announce it cannot load is strictly worse than one that stays quiet and
   * succeeds on the next tick. `error` is set for anyone who wants it, and the
   * previously fetched list is left ON SCREEN rather than cleared, because a
   * stale alert is still true and dropping it would hide a notice the customer
   * has not read yet.
   */
  const refresh = () => {
    if (!isLoggedIn.value) return Promise.resolve()

    // Every refresh — automatic or on tab focus — restarts the cycle, so the
    // next automatic one is a full interval away from the last actual fetch.
    armPoll()

    // Coalesced, not queued: a second caller joins the request already in the
    // air instead of starting one that would settle into the same list.
    if (inFlight) return inFlight

    if (!loadedOnce) loading.value = true

    inFlight = alertsService
      .get()
      .then(({ data }) => {
        error.value = null

        // No body at all is treated like `available: false` rather than like an
        // empty list, for the same reason: we were told nothing, so we say
        // nothing.
        available.value = Boolean(data) && data.available !== false

        if (!available.value) {
          alerts.value = []
          hasMore.value = false
          serverUnreadCount.value = 0
          return
        }

        /* Re-synced from the server on every poll, which is what corrects any
           drift the local adjustments in markRead/dismiss have introduced. */
        serverUnreadCount.value = Number.isFinite(Number(data?.unread_count))
          ? Math.max(0, Number(data.unread_count))
          : 0

        const list = Array.isArray(data?.alerts) ? data.alerts : []
        const head = list.map(normalize).filter(Boolean)

        /*
         * MERGED, not replaced. A poll only ever fetches the first page, so
         * assigning it straight to `alerts` would silently collapse a list the
         * reader had expanded with "View previous" — rows vanishing from under
         * an open dropdown, on a timer, for no reason they could see.
         *
         * Everything the head does not cover is kept below it, minus anything
         * the head now carries (a row can be re-read or re-titled) and minus
         * anything dismissed elsewhere. Ordering is preserved because both
         * halves arrive newest-first and the tail is strictly older.
         */
        const headIds = new Set(head.map((alert) => alert.id))
        const oldestInHead = head.length ? head[head.length - 1].id : null
        const tail = oldestInHead === null
          ? []
          : alerts.value.filter((alert) => !headIds.has(alert.id) && alert.id < oldestInHead)

        alerts.value = head.concat(tail)

        // The head's `has_more` describes the boundary at row 10, which the
        // reader has already crossed once there is a tail. See `tailExhausted`.
        hasMore.value = tail.length > 0 ? !tailExhausted : data?.has_more === true
      })
      .catch((err) => {
        error.value = getErrorMessage(
          err,
          "We couldn't check for account notices right now."
        )
        // `alerts` and `available` are untouched on purpose — see the note above.
      })
      .finally(() => {
        loadedOnce = true
        loading.value = false
        inFlight = null
      })

    return inFlight
  }

  /**
   * "View previous" — fetch the page older than the oldest row on screen and
   * APPEND it to the same scrolling list.
   *
   * Appending rather than navigating is the point: an alert history is a thing
   * you skim, and sending someone to a separate page to keep skimming costs
   * them the dropdown, their scroll position, and a route nobody asked for.
   *
   * Cursor-based (`before` = the oldest id we hold) so alerts arriving while the
   * dropdown is open cannot make the next page repeat or skip a row.
   *
   * Failure is reported to the caller rather than toasted — the link is right
   * there and pressing it again is the retry.
   *
   * @returns {Promise<{ok: boolean, message?: string}>}
   */
  const loadMore = () => {
    if (loadingMore.value || !hasMore.value || alerts.value.length === 0) {
      return Promise.resolve({ ok: true })
    }

    const before = alerts.value[alerts.value.length - 1].id
    loadingMore.value = true

    return alertsService
      .get({ before, limit: ALERTS_PAGE_SIZE })
      .then(({ data }) => {
        const list = Array.isArray(data?.alerts) ? data.alerts : []
        const known = new Set(alerts.value.map((alert) => alert.id))
        const older = list.map(normalize).filter((alert) => alert && !known.has(alert.id))

        alerts.value = alerts.value.concat(older)

        /* An empty page means exhausted whatever the flag says: a link that
           stays after it has stopped producing rows reads as broken. */
        const exhausted = older.length === 0 || data?.has_more !== true
        if (exhausted) tailExhausted = true
        hasMore.value = !exhausted

        return { ok: true }
      })
      .catch((err) => ({
        ok: false,
        message: getErrorMessage(err, "We couldn't load older notifications."),
      }))
      .finally(() => {
        loadingMore.value = false
      })
  }

  /**
   * Mark one alert as seen.
   *
   * Local-first and never re-fetched: the receipt matters to support, not to the
   * reader, so a failed POST leaves the card looking read for this session and
   * the next load will simply show it unread again. That is the mild failure.
   * Announcing it would be noise about a thing the customer never asked for.
   *
   * Called when an alert is actually SEEN or acted on — never merely because a
   * poll fetched it.
   */
  const markRead = (id) => {
    const alert = alerts.value.find((item) => item.id === id)
    if (!alert || alert.readAt) return Promise.resolve()

    alert.readAt = new Date().toISOString()
    // The badge has to move now, not at the next poll. Re-synced from the
    // server on every fetch, so an over-decrement here cannot accumulate.
    serverUnreadCount.value = Math.max(0, serverUnreadCount.value - 1)

    return alertsService.markRead(id).catch(() => {
      // Deliberately silent, and deliberately NOT rolled back: flipping the
      // unread dot back on under the reader's eyes says "you did not just read
      // this", which is both wrong and unactionable.
    })
  }

  /**
   * Remove one alert for good — OPTIMISTIC, WITH ROLLBACK.
   *
   * The card goes immediately (waiting on a round trip for a button whose whole
   * meaning is "get this off my screen" feels broken), but a failed request puts
   * it back at the position it held. The caller is told, so it can say so.
   * Silently swallowing this is the one outcome that is not allowed: the
   * customer would believe the notice was gone and meet it again on reload.
   *
   * @returns {Promise<{ok: boolean, message?: string}>}
   */
  const dismiss = (id) => {
    const index = alerts.value.findIndex((item) => item.id === id)
    if (index === -1) return Promise.resolve({ ok: true })

    const removed = alerts.value[index]
    const wasUnread = !removed.readAt
    alerts.value.splice(index, 1)
    // Dismissing an unread notice retires it unread, so the badge owes one
    // fewer. Restored below if the request fails and the row comes back.
    if (wasUnread) serverUnreadCount.value = Math.max(0, serverUnreadCount.value - 1)

    return alertsService
      .dismiss(id)
      .then(() => ({ ok: true }))
      .catch((err) => {
        // Re-inserted at its original index rather than appended: an alert that
        // reappears at the bottom of the stack reads as a NEW one.
        const stillGone = alerts.value.every((item) => item.id !== id)
        if (stillGone) {
          alerts.value.splice(Math.min(index, alerts.value.length), 0, removed)
          // The row is back, so the badge owes it again — otherwise a failed
          // dismiss leaves an unread notice that the bell no longer counts.
          if (wasUnread) serverUnreadCount.value += 1
        }

        return {
          ok: false,
          message: getErrorMessage(err, "We couldn't dismiss that notice."),
        }
      })
  }

  const onVisibilityChange = () => {
    if (!isLoggedIn.value) return

    if (document.hidden) {
      suspend()
      return
    }

    refresh()
  }

  watch(
    isLoggedIn,
    (loggedIn) => {
      if (!loggedIn) {
        suspend()
        // The next account must not inherit this one's notices, even for the
        // frame before its own first fetch settles.
        alerts.value = []
        available.value = true
        error.value = null
        hasMore.value = false
        // The badge must go with the list. Leaving this behind would greet the
        // next account with the previous one's unread count.
        serverUnreadCount.value = 0
        loadedOnce = false
        tailExhausted = false
        return
      }

      /* A tab that loads in the background — restored session, opened in a new
         tab, prerender — arms nothing and fetches nothing. `visibilitychange`
         starts it the moment it is actually looked at. */
      if (document.hidden) {
        suspend()
        return
      }

      refresh()
    },
    { immediate: true }
  )

  document.addEventListener('visibilitychange', onVisibilityChange, false)

  onUnmounted(() => {
    document.removeEventListener('visibilitychange', onVisibilityChange, false)
    suspend()
    inFlight = null
    // Whoever created the singleton is going away, so the next caller builds a
    // fresh one rather than adopting a controller with no timer. In practice
    // this only fires when the whole app unmounts.
    controller = null
  })

  return {
    alerts,
    hasAlerts,
    unreadCount,
    loading,
    loadingMore,
    hasMore,
    error,
    available,
    refresh,
    loadMore,
    markRead,
    dismiss,
  }
}

/**
 * The app-wide account-alerts store.
 *
 * Call it once from App.vue to START it — that call fixes the poller's lifetime
 * to the app's, which is the same reason useVoteActivity is called there. Every
 * later call (the panels) gets the same controller back and is a pure consumer.
 */
export function useAlerts() {
  if (!controller) controller = createController()
  return controller
}

export default useAlerts
